import { getCampaignLeadCostQueryOptions } from "@/api/campaign.api";
import { creditsQueryOptions } from "@/api/credits.api";
import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import {
  CompletionEmailForm,
  completionEmailFormSchema,
} from "@/components/campaign/setup/delivery/CompletionEmailForm";
import {
  ConfigureIntegrationForm,
  configureIntegrationFormSchema,
} from "@/components/campaign/setup/delivery/ConfigureIntegrationForm";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { IconCircle } from "@/components/generic/IconCircle";
import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { getCampaignUpdateCreditStats } from "@/services/credits.service";
import { Campaign } from "@/types/campaign";
import {
  StepperSubmitHandler,
  useStepperSubmitHandlerState,
} from "@/utils/stepper";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useBlocker } from "@tanstack/react-router";
import { useCallback, useMemo, useRef } from "react";
import z from "zod";
import { Route as BuyCreditsRoute } from "./buy-credits";

const historyStateSchema = z
  .union([
    z.object({
      type: z.literal("Excel"),
      values: completionEmailFormSchema,
    }),
    z.object({
      type: z.literal("Integration"),
      exportSystem: z.string(),
      exportMode: z.enum(["SalesAutomatic", "SalesManual"]),
      values: configureIntegrationFormSchema,
    }),
  ])
  .and(
    z.object({
      autoSubmit: z.boolean(),
    }),
  )
  .optional()
  .catch(undefined);

function parseHistoryState(historyState: z.infer<typeof historyStateSchema>) {
  return historyStateSchema.parse(historyState);
}

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/delivery/",
)({
  component: RouteComponent,
  beforeLoad: ({ location }) => {
    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );

    return {
      parseHistoryState,
      useHistoryState: () => historyState,
    };
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const { data: campaign } = useSuspenseQuery(context.campaignQueryOptions);
  const navigate = Route.useNavigate();

  const [submitHandler, setSubmitHandler] = useStepperSubmitHandlerState();

  const blocker = useBlocker({
    withResolver: true,
    shouldBlockFn: ({ next }) => {
      if (
        next.fullPath === Route.fullPath ||
        next.fullPath === BuyCreditsRoute.fullPath
      )
        return false;
      return submitHandler.isDirty ?? false;
    },
  });

  return (
    <PageHeaderProvider
      options={{
        actions: (
          <>
            <Button
              intent="secondary"
              onPress={() => {
                navigate({
                  from: Route.fullPath,
                  to: "/campaigns/$id/delivery/setup",
                });
              }}
            >
              Skift levering
            </Button>

            {campaign.exportSystem || campaign.completionEmail ? (
              <Button
                isDisabled={submitHandler.isDisabled || !submitHandler.isDirty}
                isPending={submitHandler.isPending}
                onPress={() => submitHandler.submit()}
              >
                Gem
              </Button>
            ) : null}
          </>
        ),
      }}
    >
      <div className="flex grow flex-col justify-center p-8 xl:p-14">
        {campaign.exportSystem ? (
          <IntegrationRouteComponent setSubmitHandler={setSubmitHandler} />
        ) : campaign.completionEmail ? (
          <ExcelRouteComponent setSubmitHandler={setSubmitHandler} />
        ) : (
          <LassoSalesRouteComponent />
        )}
      </div>

      <AlertModal
        isOpen={blocker.status === "blocked"}
        title="Du har ikke-gemte ændringer"
        description="Dine ændringer vil gå tabt. Er du sikker på at du vil fortsætte?"
        onClose={blocker.reset}
        onConfirm={() => {
          blocker.proceed?.();
        }}
      />
    </PageHeaderProvider>
  );
}

function IntegrationRouteComponent({
  setSubmitHandler,
}: {
  setSubmitHandler: (submitHandler: StepperSubmitHandler) => void;
}) {
  const context = Route.useRouteContext();
  const { campaign, updateCampaign } = context.useEditCampaign();
  const navigate = Route.useNavigate();

  let historyState = context.useHistoryState();
  if (historyState?.type !== "Integration") historyState = undefined;

  const exportSystem = useRef(
    historyState?.exportSystem || campaign.exportSystem,
  ).current;
  const exportMode = useRef(
    historyState?.exportMode || campaign.exportMode,
  ).current;

  if (!exportSystem || !exportMode)
    throw Error("Export system and mode must be defined for integration route");

  const defaultValues = useMemo(
    () => ({
      exportInput: campaign.exportInput ?? {},
    }),
    [campaign.exportInput],
  );

  const values = useMemo(
    () => historyState?.values ?? defaultValues,
    [defaultValues, historyState?.values],
  );

  return (
    <ConfigureIntegrationForm
      exportSystem={exportSystem}
      exportMode={exportMode}
      defaultValues={defaultValues}
      values={values}
      updateValues={async (values) => {
        if (!historyState?.autoSubmit) {
          const campaignWithNewValues: Campaign = {
            ...campaign,
            completionEmail: null,
            exportSystem,
            exportMode,
            ...values,
          };

          const [credits, campaignLeadCost, updatedCampaignLeadCost] =
            await Promise.all([
              context.queryClient.ensureQueryData(creditsQueryOptions),
              context.queryClient.ensureQueryData(
                getCampaignLeadCostQueryOptions(campaign),
              ),
              context.queryClient.ensureQueryData(
                getCampaignLeadCostQueryOptions(campaignWithNewValues),
              ),
            ]);

          const {
            updatedCreditsNeededRestOfPeriod,
            updatedCreditsNeededNextPeriod,
          } = getCampaignUpdateCreditStats({
            credits,
            campaignLeadCost,
            updatedCampaignLeadCost,
          });

          const neededCredits = Math.max(
            updatedCreditsNeededRestOfPeriod,
            updatedCreditsNeededNextPeriod,
          );

          if (neededCredits) {
            const newHistoryState: ReturnType<typeof parseHistoryState> = {
              type: "Integration",
              exportSystem,
              exportMode,
              values,
              autoSubmit: false,
            };

            await navigate({
              replace: true,
              state: {
                routeStates: {
                  [Route.id]: newHistoryState,
                },
              },
            });

            await navigate({
              to: BuyCreditsRoute.to,
              state: {
                routeStates: {
                  [BuyCreditsRoute.id]: {
                    neededCredits,
                  },
                  [Route.id]: newHistoryState,
                },
              },
            });
            return;
          }
        }

        await updateCampaign({
          exportSystem: exportSystem,
          exportMode: exportMode,
          exportInput: values.exportInput,
        });
      }}
      setSubmitHandler={setSubmitHandler}
      autoSubmit={historyState?.autoSubmit}
    />
  );
}

function ExcelRouteComponent({
  setSubmitHandler,
}: {
  setSubmitHandler: (submitHandler: StepperSubmitHandler) => void;
}) {
  const context = Route.useRouteContext();
  const { campaign, updateCampaign } = context.useEditCampaign();
  const navigate = Route.useNavigate();

  let historyState = context.useHistoryState();
  if (historyState?.type !== "Excel") historyState = undefined;

  const defaultValues = useMemo(
    () => ({
      completionEmail: campaign.completionEmail || "",
    }),
    [campaign.completionEmail],
  );

  const values = useMemo(
    () => historyState?.values ?? defaultValues,
    [historyState?.values, defaultValues],
  );

  return (
    <CompletionEmailForm
      defaultValues={defaultValues}
      values={values}
      updateValues={useCallback(
        async (values) => {
          if (!historyState?.autoSubmit && campaign.status === "Active") {
            const campaignWithNewValues: Campaign = {
              ...campaign,
              exportSystem: null,
              exportMode: null,
              exportInput: null,
              ...values,
            };

            const [credits, campaignLeadCost, updatedCampaignLeadCost] =
              await Promise.all([
                context.queryClient.ensureQueryData(creditsQueryOptions),
                context.queryClient.ensureQueryData(
                  getCampaignLeadCostQueryOptions(campaign),
                ),
                context.queryClient.ensureQueryData(
                  getCampaignLeadCostQueryOptions(campaignWithNewValues),
                ),
              ]);

            const {
              updatedCreditsNeededRestOfPeriod,
              updatedCreditsNeededNextPeriod,
            } = getCampaignUpdateCreditStats({
              credits,
              campaignLeadCost,
              updatedCampaignLeadCost,
            });

            const neededCredits = Math.max(
              updatedCreditsNeededRestOfPeriod,
              updatedCreditsNeededNextPeriod,
            );

            if (neededCredits) {
              const newHistoryState: ReturnType<typeof parseHistoryState> = {
                type: "Excel",
                values,
                autoSubmit: false,
              };

              await navigate({
                replace: true,
                state: {
                  routeStates: {
                    [Route.id]: newHistoryState,
                  },
                },
              });

              await navigate({
                to: BuyCreditsRoute.to,
                state: {
                  routeStates: {
                    [BuyCreditsRoute.id]: {
                      neededCredits,
                    },
                    [Route.id]: newHistoryState,
                  },
                },
              });
              return;
            }
          }

          await updateCampaign({
            completionEmail: values.completionEmail,
          });
        },
        [
          campaign,
          context.queryClient,
          historyState?.autoSubmit,
          navigate,
          updateCampaign,
        ],
      )}
      setSubmitHandler={setSubmitHandler}
      autoSubmit={historyState?.autoSubmit}
    />
  );
}

function LassoSalesRouteComponent() {
  const { id } = Route.useParams();

  return (
    <div className="flex flex-col items-center gap-y-6">
      <IconCircle icon="monitor-smartphone" />

      <SegmentHeader
        className="max-w-90"
        title="Leads håndteres i Lasso Sales"
        description="Alle leads som genereres i denne kampagne modtages og håndteres direkte i Lasso Sales."
      />

      <Button
        intent="secondary"
        className="mt-4"
        link={{
          to: "/campaigns/$id",
          params: { id: id },
        }}
      >
        Vis leads
      </Button>
    </div>
  );
}
